import { MouseEvent, useContext, useState, ReactNode } from 'react';

import { AccordionContext, SelectableContext } from './accordion.context';
import * as Styled from './accordion-new.style';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

type Props = {
  title?: string;
  children: ReactNode;
  labelComponent?: ReactNode;
  withDividers?: boolean;
  displayExpanded?: boolean;
  ariaLabel?: string;
  headingLevel?: React.ElementType;
  contentPaddingBottom?: number;
  onClick?: (e: MouseEvent, isExpanded: boolean) => void;
};

export const AccordionToggleNew = ({
  title,
  children,
  ariaLabel,
  withDividers,
  labelComponent,
  displayExpanded = false,
  headingLevel,
  contentPaddingBottom,
  onClick,
}: Props) => {
  const { id, onHeaderKeyDown, onToggle } = useContext(AccordionContext);
  const { index } = useContext(SelectableContext);
  const [isExpanded, setIsExpanded] = useState<boolean>(displayExpanded);

  return (
    <Styled.AccordionPanel
      data-selector="accordion-panel"
      $withDividers={withDividers}
    >
      <Styled.AccordionHeading as={headingLevel}>
        <Styled.AccordionButton
          data-accordion-id={id}
          className="accordion-trigger"
          id={`${id}-header-${index}`}
          aria-expanded={isExpanded}
          aria-controls={`${id}-content-${index}`}
          aria-label={ariaLabel || undefined}
          onClick={e => {
            setIsExpanded(!isExpanded);
            if (onClick) onClick(e, !isExpanded);
            onToggle(!isExpanded);
          }}
          onKeyDown={e => onHeaderKeyDown && onHeaderKeyDown(e)}
        >
          {labelComponent ? (
            labelComponent
          ) : (
            <Styled.AccordionLabel data-selector="content-section-title">
              {title}
            </Styled.AccordionLabel>
          )}
          <Styled.AccordionButtonIcon $isExpanded={isExpanded}>
            <IconDropdown width={16} height={16} />
          </Styled.AccordionButtonIcon>
        </Styled.AccordionButton>
      </Styled.AccordionHeading>
      <Styled.AccordionContent
        id={`${id}-content-${index}`}
        role="region"
        aria-labelledby={`${id}-header-${index}`}
        $isExpanded={isExpanded}
        tabIndex={-1}
        $paddingBottom={contentPaddingBottom}
        data-selector="accordion-content"
      >
        {children}
      </Styled.AccordionContent>
    </Styled.AccordionPanel>
  );
};

export default AccordionToggleNew;
